import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, ShieldAlert } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requiredPermission?: string;
  /** If true, check access_editor permission and redirect kiosk-only users */
  checkEditorAccess?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requiredPermission,
  checkEditorAccess = false,
}) => {
  const { user, loading, isAdmin, isApproved, hasPermission, mustChangePassword } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-workspace">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if user is approved (admins are always approved)
  if (!isApproved && !isAdmin) {
    return <Navigate to="/auth" replace />;
  }

  // Force password change before accessing any protected route
  if (mustChangePassword) {
    return <Navigate to="/auth" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission as any)) {
    return <Navigate to="/" replace />;
  }

  // Check editor access: redirect kiosk-only users, block users without any access
  if (checkEditorAccess && !isAdmin) {
    const canAccessEditor = hasPermission('access_editor' as any);
    const canUseKiosk = hasPermission('use_kiosk' as any);

    if (!canAccessEditor && canUseKiosk) {
      // Kiosk-only user: redirect to kiosk
      return <Navigate to="/kiosk" replace />;
    }

    if (!canAccessEditor) {
      // No access at all - show access denied
      return (
        <div className="min-h-screen flex items-center justify-center bg-workspace">
          <div className="text-center space-y-4 max-w-md mx-auto p-8">
            <ShieldAlert className="w-16 h-16 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">Access Denied</h1>
            <p className="text-muted-foreground">
              Your account does not have permission to access the label editor.
              Please contact an administrator to request access.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
